import React from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
  ChartOptions
} from 'chart.js'
import { Line, Bar } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
)

interface MarketDepthChartProps {
  market: any
  enhancedData: any
}

const MarketDepthChart: React.FC<MarketDepthChartProps> = ({ market, enhancedData }) => {
  if (!enhancedData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--sacred-text-dim)' }}>
        No chart data available
      </div>
    )
  }

  const { orderBook, priceHistory } = enhancedData

  // Price history data
  if (priceHistory?.length > 0) {
    const chartData = {
      labels: priceHistory.map((point: any) => {
        const date = new Date(point.timestamp)
        return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`
      }),
      datasets: [{
        label: 'Price',
        data: priceHistory.map((point: any) => point.price * 100),
        borderColor: 'var(--sacred-accent)',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1
      }]
    }

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'var(--sacred-bg)',
          borderColor: 'var(--sacred-border)',
          borderWidth: 1,
          titleColor: 'var(--sacred-text)',
          bodyColor: 'var(--sacred-text)',
          titleFont: { size: 11 },
          bodyFont: { size: 11 },
          padding: 8,
          callbacks: {
            label: (context) => `Price: ${context.parsed.y.toFixed(1)}%`
          }
        }
      },
      scales: {
        x: {
          ticks: { 
            color: 'var(--sacred-text-dim)', 
            font: { size: 10 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 6
          },
          grid: { color: 'var(--sacred-border)', drawBorder: false }
        },
        y: {
          min: 0,
          max: 100,
          ticks: {
            color: 'var(--sacred-text-dim)',
            font: { size: 10 },
            callback: (value) => `${value}¢`
          },
          grid: { color: 'var(--sacred-border)', drawBorder: false }
        }
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <h4 style={{ 
          fontSize: '0.7rem', 
          textTransform: 'uppercase', 
          color: 'var(--sacred-text-dim)',
          margin: 0
        }}>
          Price History ({market.question})
        </h4>
        <div style={{ height: '200px' }}>
          <Line data={chartData} options={options} />
        </div>
      </div>
    )
  }

  // Polymarket order book depth chart
  if (market.platform === 'polymarket' && orderBook?.bids && orderBook?.asks) {
    const prices: number[] = []
    const bidVolumes: number[] = []
    const askVolumes: number[] = []
    
    let bidVolume = 0
    let askVolume = 0
    
    // Process bids and asks
    const allPrices = new Set<number>()
    orderBook.bids.forEach(([price]: [number, number]) => allPrices.add(price * 100))
    orderBook.asks.forEach(([price]: [number, number]) => allPrices.add(price * 100))
    
    const sortedPrices = Array.from(allPrices).sort((a, b) => a - b)
    
    sortedPrices.forEach(price => {
      const bid = orderBook.bids.find(([p]: [number, number]) => p * 100 === price)
      const ask = orderBook.asks.find(([p]: [number, number]) => p * 100 === price)
      
      if (bid) bidVolume += bid[1]
      if (ask) askVolume += ask[1]
      
      prices.push(price)
      bidVolumes.push(bidVolume)
      askVolumes.push(askVolume)
    })

    const chartData = {
      labels: prices.map(p => `${p}¢`),
      datasets: [
        {
          label: 'Bid Volume',
          data: bidVolumes,
          borderColor: 'var(--sacred-accent)',
          backgroundColor: 'rgba(0, 255, 0, 0.1)',
          fill: true,
          stepped: 'after' as const,
          tension: 0
        },
        {
          label: 'Ask Volume',
          data: askVolumes,
          borderColor: 'var(--sacred-error)',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          fill: true,
          stepped: 'after' as const,
          tension: 0
        }
      ]
    }

    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { 
          display: true,
          position: 'top',
          labels: {
            color: 'var(--sacred-text)',
            font: { size: 10 },
            boxWidth: 15
          }
        },
        tooltip: {
          backgroundColor: 'var(--sacred-bg)',
          borderColor: 'var(--sacred-border)',
          borderWidth: 1,
          titleColor: 'var(--sacred-text)',
          bodyColor: 'var(--sacred-text)',
          titleFont: { size: 11 },
          bodyFont: { size: 11 },
          padding: 8,
          callbacks: {
            label: (context) => `${context.dataset.label}: ${context.parsed.y.toFixed(2)}`
          }
        }
      },
      scales: {
        x: {
          ticks: { 
            color: 'var(--sacred-text-dim)', 
            font: { size: 10 },
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit: 10
          },
          grid: { color: 'var(--sacred-border)', drawBorder: false }
        },
        y: {
          ticks: { 
            color: 'var(--sacred-text-dim)', 
            font: { size: 10 },
            callback: (value) => value.toLocaleString()
          },
          grid: { color: 'var(--sacred-border)', drawBorder: false }
        }
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <h4 style={{ 
          fontSize: '0.7rem', 
          textTransform: 'uppercase', 
          color: 'var(--sacred-text-dim)',
          margin: 0
        }}>
          Order Book Depth
        </h4>
        <div style={{ height: '200px' }}>
          <Line data={chartData} options={options} />
        </div>
      </div>
    )
  }

  // Kalshi YES/NO contract depth
  if (market.platform === 'kalshi' && orderBook?.yes_orders && orderBook?.no_orders) {
    const chartData = {
      labels: ['YES Orders', 'NO Orders'],
      datasets: [{
        label: 'Contracts',
        data: [
          orderBook.yes_orders.reduce((sum: number, order: any) => sum + order.quantity, 0),
          orderBook.no_orders.reduce((sum: number, order: any) => sum + order.quantity, 0)
        ],
        backgroundColor: ['var(--sacred-accent)', 'var(--sacred-error)'],
        borderColor: ['var(--sacred-accent)', 'var(--sacred-error)'],
        borderWidth: 1
      }]
    }

    const options: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: 'var(--sacred-bg)',
          borderColor: 'var(--sacred-border)',
          borderWidth: 1,
          titleColor: 'var(--sacred-text)',
          bodyColor: 'var(--sacred-text)',
          titleFont: { size: 11 },
          bodyFont: { size: 11 },
          padding: 8,
          callbacks: {
            label: (context) => `Contracts: ${context.parsed.y.toFixed(0)}`
          }
        }
      },
      scales: {
        x: {
          ticks: { 
            color: 'var(--sacred-text-dim)', 
            font: { size: 10 }
          },
          grid: { display: false }
        },
        y: {
          ticks: { 
            color: 'var(--sacred-text-dim)', 
            font: { size: 10 },
            callback: (value) => value.toLocaleString()
          },
          grid: { color: 'var(--sacred-border)', drawBorder: false }
        }
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <h4 style={{ 
          fontSize: '0.7rem', 
          textTransform: 'uppercase', 
          color: 'var(--sacred-text-dim)',
          margin: 0
        }}>
          Contract Depth
        </h4>
        <div style={{ height: '200px' }}>
          <Bar data={chartData} options={options} />
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--sacred-text-dim)' }}>
      No chart data available for this market
    </div>
  )
}

export default MarketDepthChart